import { Unit } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { Row } from "views/applications-id-report/components/Row";
import { useState } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { useFormTyped } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import addressApi from "api/landlord/address";
import { type Register } from "shared/hooks";
import { PlanForm } from "shared/app/PlanForm";
import * as yup from "yup";

interface PlanSectionProps {
  propertyId: number;
  unitId: number;
  plan: Unit["plan"];
  dimensions?: string;
}

const planValidationSchema = yup.object({
  sqft: yup
    .number()
    .min(0, "Size must be greater than or equal to 0")
    .max(10000, "Size must be less than or equal to 10,000 sq.ft")
    .optional(),
  beds: yup
    .number()
    .integer("Number of beds must be a whole number")
    .min(0, "Number of beds must be greater than or equal to 0")
    .max(10, "Number of beds must be less than or equal to 10")
    .optional(),
  baths: yup
    .number()
    .min(0, "Number of baths must be greater than or equal to 0")
    .max(10, "Number of baths must be less than or equal to 10")
    .optional()
});

const EditPlanForm = ({
  onClose,
  unitId,
  propertyId,
  initialValues
}: {
  onClose: () => void;
  propertyId: number;
  unitId: number;
  initialValues: {
    sqft?: number;
    beds?: number;
    baths?: number;
    dimensions?: string;
  };
}) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(
    initialValues,
    planValidationSchema
  );

  const { mutate: updatePlan, isPending } = useMutation({
    mutationFn: () =>
      addressApi.editUnit(unitId, {
        plan: {
          sqft: values.sqft || undefined,
          beds: values.beds || undefined,
          baths: values.baths || undefined
        },
        dimensions: values.dimensions || undefined
      }),
    onSuccess: () => {
      toast.success("Plan updated");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update plan: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => updatePlan()
  });

  return (
    <PlanForm
      register={register as unknown as Register}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    />
  );
};
export const PlanSection = ({
  propertyId,
  unitId,
  plan,
  dimensions
}: PlanSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing)
    return (
      <EditPlanForm
        onClose={() => setIsEditing(false)}
        propertyId={propertyId}
        unitId={unitId}
        initialValues={{
          sqft: plan.sqft,
          beds: plan.beds,
          baths: plan.baths,
          dimensions
        }}
      />
    );

  return (
    <SectionCard
      title="Plan"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        <Row title="Size, sq.ft" value={plan.sqft} />
        <Row title="Beds" value={plan.beds} />
        <Row title="Baths" value={plan.baths} />
        <Row title="Dimensions" value={dimensions} />
      </div>
    </SectionCard>
  );
};
